import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const SidebarContainer = styled.aside`
  background: white;
  box-shadow: 0 5px 20px rgba(0,0,0,0.15);
  padding: 30px 0;
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  width: 280px;
  z-index: 1000;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    
    &.open {
      transform: translateX(0);
    }
  }
`;

const SidebarHeader = styled.div`
  padding: 0 30px 30px;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 30px;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 10px;
`;

const LogoIcon = styled.div`
  font-size: 2.5em;
  color: #667eea;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`;

const CompanyName = styled.div`
  font-size: 1.8em;
  font-weight: 600;
  color: #2c3e50;
`;

const CompanyTagline = styled.div`
  color: #7f8c8d;
  font-size: 0.9em;
`;

const NavMenu = styled.nav`
  list-style: none;
  padding: 0 20px;
`;

const NavItem = styled.li`
  margin-bottom: 5px;
`;

const NavLink = styled.button`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px 20px;
  color: #2c3e50;
  text-decoration: none;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  border-radius: 12px;
  transition: all 0.3s ease;
  font-weight: 500;
  cursor: pointer;
  font-size: 1rem;
  
  &:hover,
  &.active {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    transform: translateX(5px);
  }
  
  i {
    width: 20px;
    text-align: center;
  }
`;

const UserSection = styled.div`
  position: absolute;
  bottom: 30px;
  left: 0;
  right: 0;
  padding: 20px 30px;
  border-top: 1px solid #e9ecef;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
`;

const UserDetails = styled.div`
  h4 {
    font-size: 1em;
    margin-bottom: 2px;
    color: #2c3e50;
  }
  
  p {
    font-size: 0.8em;
    color: #7f8c8d;
  }
`;

const LogoutButton = styled.button`
  width: 100%;
  padding: 10px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  color: #2c3e50;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9em;
  
  &:hover {
    background: #e74c3c;
    color: white;
    border-color: #e74c3c;
  }
`;

const Sidebar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState('dashboard');

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'fas fa-tachometer-alt', path: '/' },
    { id: 'live-stream', label: 'Live Stream', icon: 'fas fa-video', path: '/live-stream' },
    { id: 'analytics', label: 'Analytics', icon: 'fas fa-chart-line', path: '/analytics' },
    { id: 'settings', label: 'Settings', icon: 'fas fa-cog', path: '/settings' }
  ];

  const handleMenuClick = (menuItem) => {
    setActiveMenu(menuItem.id);
    navigate(menuItem.path);
  };

  const isActive = (menuId) => {
    if (menuId === 'dashboard' && location.pathname === '/') {
      return true;
    }
    return location.pathname === menuId;
  };

  return (
    <SidebarContainer>
      <SidebarHeader>
        <LogoSection>
          <LogoIcon>
            <i className="fas fa-broadcast-tower"></i>
          </LogoIcon>
          <div>
            <CompanyName>Holy TV</CompanyName>
            <CompanyTagline>Broadcasting</CompanyTagline>
          </div>
        </LogoSection>
      </SidebarHeader>

      <NavMenu>
        {menuItems.map((item) => (
          <NavItem key={item.id}>
            <NavLink
              onClick={() => handleMenuClick(item)}
              className={isActive(item.id) ? 'active' : ''}
            >
              <i className={item.icon}></i>
              {item.label}
            </NavLink>
          </NavItem>
        ))}
      </NavMenu>

      <UserSection>
        <UserInfo>
          <UserAvatar>
            {user?.username?.charAt(0).toUpperCase() || 'A'}
          </UserAvatar>
          <UserDetails>
            <h4>{user?.username?.charAt(0).toUpperCase() + user?.username?.slice(1) || 'Admin'}</h4>
            <p>Administrator</p>
          </UserDetails>
        </UserInfo>
        <LogoutButton onClick={onLogout}>
          <i className="fas fa-sign-out-alt"></i>
          Logout
        </LogoutButton>
      </UserSection>
    </SidebarContainer>
  );
};

export default Sidebar; 